"use client"

import type { PropsWithChildren } from "react"
import { useEffect, useRef } from "react"

export interface UbHtmlEmbedStoryblok {
  code?: string
  acknowledge_risk?: boolean
  allow_production_execution?: boolean
  hide_mobile?: boolean
  component: "ub_html_embed"
  _uid: string
  [key: string]: unknown
}

interface UbHtmlEmbedClientProps {
  blok: UbHtmlEmbedStoryblok
  isProduction: boolean
  storyblokEditableProps: Record<string, unknown>
}

const disallowedFullPageTagPatterns = [
  { label: "<!doctype>", pattern: /<!doctype\b/i },
  { label: "<html>", pattern: /<html\b/i },
  { label: "</html>", pattern: /<\/html\s*>/i },
  { label: "<head>", pattern: /<head\b/i },
  { label: "</head>", pattern: /<\/head\s*>/i },
  { label: "<body>", pattern: /<body\b/i },
  { label: "</body>", pattern: /<\/body\s*>/i },
]

function ErrorMessage({
  children,
  storyblokEditableProps,
}: PropsWithChildren<Pick<UbHtmlEmbedClientProps, "storyblokEditableProps">>) {
  return (
    <div
      {...storyblokEditableProps}
      className="border border-red-300 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800"
    >
      {children}
    </div>
  )
}

export default function UbHtmlEmbedClient({ blok, isProduction, storyblokEditableProps }: UbHtmlEmbedClientProps) {
  const ref = useRef<HTMLDivElement>(null)
  const code = blok.code ?? ""
  const disallowedFullPageTag = disallowedFullPageTagPatterns.find(({ pattern }) => pattern.test(code))
  const productionExecutionBlocked = isProduction && !blok.allow_production_execution

  useEffect(() => {
    if (!ref.current || !blok.acknowledge_risk || productionExecutionBlocked || disallowedFullPageTag) return

    const scripts = ref.current.querySelectorAll("script")

    scripts.forEach((oldScript) => {
      const newScript = document.createElement("script")

      Array.from(oldScript.attributes).forEach((attr) => {
        newScript.setAttribute(attr.name, attr.value)
      })

      newScript.textContent = oldScript.textContent
      oldScript.replaceWith(newScript)
    })
  }, [blok.acknowledge_risk, code, disallowedFullPageTag, productionExecutionBlocked])

  if (!blok.acknowledge_risk) {
    return (
      <ErrorMessage storyblokEditableProps={storyblokEditableProps}>
        HTML Embed ist deaktiviert. Bitte bestaetige zuerst, dass dieser Block beliebigen Code ausfuehren kann.
      </ErrorMessage>
    )
  }

  if (productionExecutionBlocked) {
    return (
      <ErrorMessage storyblokEditableProps={storyblokEditableProps}>
        HTML Embed ist in Production deaktiviert. Bitte aktiviere die Production-Freigabe fuer diesen Block.
      </ErrorMessage>
    )
  }

  if (disallowedFullPageTag) {
    return (
      <ErrorMessage storyblokEditableProps={storyblokEditableProps}>
        HTML Embed enthaelt {disallowedFullPageTag.label}. Bitte nur Fragment-Code ohne Doctype, HTML-, Head- oder
        Body-Tags einfuegen.
      </ErrorMessage>
    )
  }

  return <div ref={ref} {...storyblokEditableProps} dangerouslySetInnerHTML={{ __html: code }} />
}
